import * as React from 'react';
import { t } from '@reos/i18n';
import { Link } from '@tanstack/react-router';
import { AddressAutocomplete } from './AddressAutocomplete.js';
import { emptyDealAddress, type DealAddress } from '../lib/wizard-model.js';
import { propertyAddressOf } from '../lib/address-query.js';
import {
  changePropertyAddress,
  errorMessage,
  type ChangeAddressResult,
  type StaleAddressOutput,
} from '../lib/properties.js';

/**
 * Krok PROP7B bod 2 — OPRAVA ADRESY NA KARTE NEMOVITOSTI (nalez c. 23).
 *
 * PROP7 udelal opravu v API (`PATCH /v1/properties/:id/address`), ale makler
 * k ni nemel cestu: spatna adresa z naboru se dal opravovala jen pres API.
 *
 * TRI VECI, KTERE OBRAZOVKA MUSI UMET:
 *  1. NAPOVEDA JE TATAZ JAKO PRI ZAKLADANI (`AddressAutocomplete` nad nasim
 *     registrem RUIAN) a adresa se posila ve tvaru zalozeni
 *     (`propertyAddressOf`) — jinak by „zadej adresu" a „oprav adresu" mely
 *     jinou predstavu o tom, co je platna adresa, a dedupe `normalized_key`
 *     by stejnou adresu ulozil dvakrat.
 *  2. DUVOD JE POVINNY (server ho vyzaduje, min. 3 znaky): oprava meni, co
 *     uvidi verejna stranka i rozpracovana smlouva, takze audit ma vedet proc.
 *  3. CO SE SAMO NEOPRAVI, SE UKAZE. `staleOutputs` z odpovedi jsou vystupy
 *     se SNAPSHOTEM stare adresy (podepsana smlouva, publikovany inzerat) —
 *     makler je musi aktualizovat sam, proto u nich stoji odkaz, kam jit.
 */

const cs = { locale: 'cs' } as const;

/** Kam vede odkaz u vystupu, ktery nese starou adresu. */
function StaleOutputLink({ output }: { readonly output: StaleAddressOutput }): React.JSX.Element {
  if (output.kind === 'signed_contract') {
    return (
      <Link to="/smlouvy/$id" params={{ id: output.id }} className="underline">
        {t('webAdmin.propertyDetail.addressFix.staleContract', { ...cs, values: { label: output.label } })}
      </Link>
    );
  }
  /*
   * Publikovany inzerat: `staleOutputs.id` je radek publikace
   * (`publications.id`), ne zakazka — obrazovku detailu publikace admin nema,
   * takze odkaz vede na „Portály", kde makler inzerat najde a odesle znovu.
   */
  return (
    <Link to="/portaly" className="underline">
      {t('webAdmin.propertyDetail.addressFix.staleListing', { ...cs, values: { label: output.label } })}
    </Link>
  );
}

export function PropertyAddressFix({
  propertyId,
  currentAddress,
  onChanged,
}: {
  readonly propertyId: string;
  /** Adresa, kterou karta ukazuje — predvyplni pole, aby se jen opravovalo. */
  readonly currentAddress: string | null;
  /** Karta si po oprave znovu nacte detail (novou adresu ukazuje hlavicka). */
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [address, setAddress] = React.useState<DealAddress>(() => ({
    ...emptyDealAddress(),
    query: currentAddress ?? '',
  }));
  const [reason, setReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<ChangeAddressResult | null>(null);

  const canSave = address.query.trim().length >= 3 && reason.trim().length >= 3 && !saving;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const changed = await changePropertyAddress(
        propertyId,
        propertyAddressOf(
          address.query.trim(),
          address.lat === null || address.lon === null ? null : { lat: address.lat, lon: address.lon },
        ),
        reason.trim(),
      );
      setResult(changed);
      setOpen(false);
      setReason('');
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3" data-testid="property-address-fix">
      {!open ? (
        <button
          type="button"
          className="rh-btn w-full md:w-auto"
          data-testid="property-address-fix-open"
          onClick={() => {
            setResult(null);
            setAddress({ ...emptyDealAddress(), query: currentAddress ?? '' });
            setOpen(true);
          }}
        >
          {t('webAdmin.propertyDetail.addressFix.button', cs)}
        </button>
      ) : (
        <form onSubmit={(event) => void submit(event)} data-testid="property-address-fix-form">
          <p className="text-sm font-semibold mb-2">{t('webAdmin.propertyDetail.addressFix.title', cs)}</p>
          {/* Mobil: pole pod sebou a tlacitka na celou sirku; desktop ve dvou sloupcich. */}
          <div className="flex flex-col md:flex-row gap-2">
            <div className="min-w-0 md:flex-1">
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                testIdPrefix="property-address-fix-address"
              />
            </div>
            <div className="min-w-0 md:flex-1">
              <label className="text-sm" htmlFor="property-address-fix-reason">
                {t('webAdmin.propertyDetail.addressFix.reasonLabel', cs)}
              </label>
              <input
                id="property-address-fix-reason"
                className="rh-input"
                style={{ width: '100%' }}
                data-testid="property-address-fix-reason"
                value={reason}
                placeholder={t('webAdmin.propertyDetail.addressFix.reasonPlaceholder', cs)}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          </div>
          {error !== null ? (
            <p role="alert" className="text-sm mt-2" style={{ color: 'var(--rh-danger)' }} data-testid="property-address-fix-error">
              {error}
            </p>
          ) : null}
          <div className="flex flex-col md:flex-row gap-2 mt-2">
            <button
              type="submit"
              className="rh-btn w-full md:w-auto"
              disabled={!canSave}
              data-testid="property-address-fix-save"
            >
              {saving
                ? t('webAdmin.propertyDetail.addressFix.saving', cs)
                : t('webAdmin.propertyDetail.addressFix.save', cs)}
            </button>
            <button
              type="button"
              className="rh-btn w-full md:w-auto"
              data-testid="property-address-fix-cancel"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              {t('webAdmin.propertyDetail.addressFix.cancel', cs)}
            </button>
          </div>
        </form>
      )}

      {result !== null ? (
        <div className="mt-3" data-testid="property-address-fix-result">
          <p role="status" className="text-sm">
            {result.changed
              ? t('webAdmin.propertyDetail.addressFix.changed', {
                  ...cs,
                  values: { address: result.property.address?.formatted ?? '' },
                })
              : t('webAdmin.propertyDetail.addressFix.unchanged', cs)}
          </p>
          {result.staleOutputs.length > 0 ? (
            <div className="mt-2" data-testid="property-address-fix-stale">
              <p className="text-sm font-semibold">
                {t('webAdmin.propertyDetail.addressFix.staleTitle', cs)}
              </p>
              <ul className="m-0 p-0 list-none text-sm">
                {result.staleOutputs.map((output) => (
                  <li key={`${output.kind}-${output.id}`} className="py-1">
                    <StaleOutputLink output={output} />
                  </li>
                ))}
              </ul>
              <p className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
                {t('webAdmin.propertyDetail.addressFix.staleNote', cs)}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
